"use client";

import { OpenAPI } from "@jfit/api-client";
import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";

import { useAuthStore } from "@/store/auth-store";

const REFRESH_PATH = "/api/refresh-token";
const REFRESH_PATH_BACKEND = "refresh-token";

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(REFRESH_PATH, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Refresh failed");
      const data = (await res.json().catch(() => ({}))) as {
        data?: { access_token?: string };
        sid?: string;
      };
      const token = data?.data?.access_token ?? data?.sid ?? null;
      if (token) {
        OpenAPI.TOKEN = token;
        useAuthStore.getState().setAccessToken(token);
      }
      return token;
    } catch {
      useAuthStore.getState().clear();
      if (typeof window !== "undefined") {
        window.location.href = "/login";
      }
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

function isRefreshRequest(url: string): boolean {
  return url.includes(REFRESH_PATH) || url.includes(REFRESH_PATH_BACKEND);
}

export function createApiAxiosInstance(): AxiosInstance {
  const instance = axios.create({
    withCredentials: true,
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken ?? OpenAPI.TOKEN;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      const status = error.response?.status;
      const url = originalRequest?.url ?? "";

      if (status === 401 && originalRequest && !originalRequest._retry && !isRefreshRequest(url)) {
        originalRequest._retry = true;
        const newToken = await refreshAccessToken();
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance.request(originalRequest);
        }
      }

      if (status === 401 && originalRequest?._retry) {
        useAuthStore.getState().clear();
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
}

let cachedInstance: AxiosInstance | null = null;

export function getApiAxiosInstance(): AxiosInstance {
  if (!cachedInstance) {
    cachedInstance = createApiAxiosInstance();
  }
  return cachedInstance;
}
